import type { ConvertableUnit } from "@/libs/units";
import {
  PSI_ATM_CONVERSION_FACTOR,
  PSI_BAR_CONVERSION_FACTOR
} from "@/libs/calculations/constants";
import {
  Atm,
  Bar,
  KiloPascals,
  Pascal,
  PoundsPerSquareInchAbsolute
} from "./pressure";
import { buildOptions } from "./buildOptions";

export const BarrelDayPsi: ConvertableUnit = {
  label: "stb/d/psi",
  toStandardUnit: stdb_psia => stdb_psia,
  toThisUnit: stdb_psia => stdb_psia
};

export const CubicMetersPerDayKiloPascal: ConvertableUnit = {
  label: "m³/d/kPa",
  toStandardUnit: m3_d_kPa =>
    m3_d_kPa / (0.158987294928 / (PSI_BAR_CONVERSION_FACTOR * 100)),
  toThisUnit: stdb_psia =>
    stdb_psia * (0.158987294928 / (PSI_BAR_CONVERSION_FACTOR * 100))
};

export const CubicMetersPerDayPascal: ConvertableUnit = {
  label: "m³/d/Pa",
  toStandardUnit: m3_d_Pa =>
    m3_d_Pa / (0.158987294928 / (PSI_BAR_CONVERSION_FACTOR * 100000)),
  toThisUnit: stdb_psia =>
    stdb_psia * (0.158987294928 / (PSI_BAR_CONVERSION_FACTOR * 100000))
};

export const CubicMetersPerDayBar: ConvertableUnit = {
  label: "m³/d/Bar",
  toStandardUnit: m3_d_Bar =>
    m3_d_Bar / (0.158987294928 / PSI_BAR_CONVERSION_FACTOR),
  toThisUnit: stdb_psia =>
    stdb_psia * (0.158987294928 / PSI_BAR_CONVERSION_FACTOR)
};

export const CubicMetersPerDayAtm: ConvertableUnit = {
  label: "m³/d/Atm",
  toStandardUnit: m3_d_Atm =>
    m3_d_Atm / (0.158987294928 / PSI_ATM_CONVERSION_FACTOR),
  toThisUnit: stdb_psia =>
    stdb_psia * (0.158987294928 / PSI_ATM_CONVERSION_FACTOR)
};

export const LitersSecondPerBar: ConvertableUnit = {
  label: "l/s/Bar",
  toStandardUnit: l_s_Bar =>
    l_s_Bar / (0.00184013073 / PSI_BAR_CONVERSION_FACTOR),
  toThisUnit: stdb_psia =>
    stdb_psia * (0.00184013073 / PSI_BAR_CONVERSION_FACTOR)
};

export const LitersMinutePerBar: ConvertableUnit = {
  label: "l/m/Bar",
  toStandardUnit: l_m_Bar =>
    l_m_Bar / (0.1104078437 / PSI_BAR_CONVERSION_FACTOR),
  toThisUnit: stdb_psia =>
    stdb_psia * (0.1104078437 / PSI_BAR_CONVERSION_FACTOR)
};

const ProductivityIndexUnitList = [
  BarrelDayPsi,
  CubicMetersPerDayKiloPascal,
  CubicMetersPerDayPascal,
  CubicMetersPerDayBar,
  CubicMetersPerDayAtm,
  LitersSecondPerBar,
  LitersMinutePerBar
];

export const ProductivityIndexUnits = {
  BarrelDayPsi,
  CubicMetersPerDayKiloPascal,
  CubicMetersPerDayPascal,
  CubicMetersPerDayBar,
  CubicMetersPerDayAtm,
  LitersSecondPerBar,
  LitersMinutePerBar,
  options: buildOptions(ProductivityIndexUnitList)
};

export function getPressureUnitFromPIUnit(piUnit: ConvertableUnit) {
  switch (piUnit) {
    case BarrelDayPsi:
      return PoundsPerSquareInchAbsolute;

    case CubicMetersPerDayKiloPascal:
      return KiloPascals;

    case CubicMetersPerDayPascal:
      return Pascal;

    case CubicMetersPerDayBar:
    case LitersSecondPerBar:
    case LitersMinutePerBar:
      return Bar;

    case CubicMetersPerDayAtm:
      return Atm;

    default:
      throw new Error("Incompatible unit passed in");
  }
}
